<template>
  <div class="mass-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$m = \rho V$</div>
    </div>
    
    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="200" viewBox="0 0 400 200">
          <!-- 支点 fulcrum -->
          <polygon points="200,160 190,190 210,190" fill="#64748b" />
          <line x1="100" y1="190" x2="300" y2="190" stroke="#334155" stroke-width="4" stroke-linecap="round" />
          
          <!-- 天平横梁 -->
          <g :transform="`translate(200, 160) rotate(${angle})`">
             <line x1="-120" y1="0" x2="120" y2="0" stroke="#94a3b8" stroke-width="6" stroke-linecap="round" />
             <!-- 左侧托盘悬挂点 -->
             <circle cx="-120" cy="0" r="3" fill="#cbd5e1" />
             <!-- 右侧托盘悬挂点 -->
             <circle cx="120" cy="0" r="3" fill="#cbd5e1" />
          </g>
          
          <!-- 左侧托盘 (保持竖直) -->
          <g :transform="`translate(${200 - 120 * Math.cos(angle * Math.PI / 180)}, ${160 - 120 * Math.sin(angle * Math.PI / 180)})`">
             <!-- 悬索 -->
             <line x1="0" y1="0" x2="-20" y2="40" stroke="#64748b" stroke-width="1.5" />
             <line x1="0" y1="0" x2="20" y2="40" stroke="#64748b" stroke-width="1.5" />
             <!-- 托盘 -->
             <path d="M-25,40 L25,40 L15,45 L-15,45 Z" fill="#475569" />
             <!-- 左侧物体 -->
             <g v-if="massA > 0">
               <rect :x="-15 * sizeA" :y="40 - 30 * sizeA" :width="30 * sizeA" :height="30 * sizeA" :fill="colorA" rx="2" />
               <text x="0" :y="40 - 15 * sizeA + 4" fill="#fff" font-size="10" text-anchor="middle" font-weight="bold">{{ massA }}kg</text>
             </g>
          </g>
          
          <!-- 右侧托盘 -->
          <g :transform="`translate(${200 + 120 * Math.cos(angle * Math.PI / 180)}, ${160 + 120 * Math.sin(angle * Math.PI / 180)})`">
             <!-- 悬索 -->
             <line x1="0" y1="0" x2="-20" y2="40" stroke="#64748b" stroke-width="1.5" />
             <line x1="0" y1="0" x2="20" y2="40" stroke="#64748b" stroke-width="1.5" />
             <!-- 托盘 -->
             <path d="M-25,40 L25,40 L15,45 L-15,45 Z" fill="#475569" />
             <!-- 右侧物体 -->
             <g v-if="massB > 0">
               <rect :x="-15 * sizeB" :y="40 - 30 * sizeB" :width="30 * sizeB" :height="30 * sizeB" :fill="colorB" rx="2" />
               <text x="0" :y="40 - 15 * sizeB + 4" fill="#fff" font-size="10" text-anchor="middle" font-weight="bold">{{ massB }}kg</text>
             </g>
          </g>
        </svg>

        <div class="hud-panel-pro">
           <div class="hud-col main">
              <div class="item big">
                 <label v-latex>左侧质量 $m_A$</label>
                 <span class="val" :style="{color: colorA}">{{ massA }} <small>kg</small></span>
              </div>
              <div class="item big">
                 <label v-latex>右侧质量 $m_B$</label>
                 <span class="val" :style="{color: colorB}">{{ massB }} <small>kg</small></span>
              </div>
           </div>
           <div class="hud-col">
              <div class="item">
                 <label>平衡状态</label>
                 <span>{{ balanceState }}</span>
              </div>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card glass">
           <div class="control-vertical-group">
              <div class="param-row">
                 <div class="s-item">
                    <label v-latex>左侧物体质量 ($m_A$)</label>
                    <el-slider v-model="massA" :min="1" :max="10" :step="1" input-size="mini"></el-slider>
                 </div>
                 <div class="s-item">
                    <label v-latex>右侧物体质量 ($m_B$)</label>
                    <el-slider v-model="massB" :min="1" :max="10" :step="1" input-size="mini"></el-slider>
                 </div>
              </div>
           </div>
        </div>
        
        <div class="discovery-card-compact" v-latex>
           <i>🔍</i>
           <p><strong>天平测质量：</strong>物体质量由其所含物质多少决定。当两端质量不相等时，受力不平衡，天平偏向质量大的一端。质量是标量，在任何惯性系中保持恒定。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MassViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      massA: 4,
      massB: 4,
      currentAngle: 0,
      animFrame: null
    }
  },
  computed: {
    targetAngle() {
       const diff = this.massB - this.massA;
       if (diff === 0) return 0;
       return diff > 0 ? Math.min(25, diff * 5) : Math.max(-25, diff * 5);
    },
    angle() {
      return this.currentAngle;
    },
    sizeA() {
       return 0.8 + (this.massA / 10) * 0.8;
    },
    sizeB() {
       return 0.8 + (this.massB / 10) * 0.8;
    },
    colorA() {
      return '#3b82f6';
    },
    colorB() {
      return '#f43f5e';
    },
    balanceState() {
       if (this.massA === this.massB) return '平衡 (m₁ = m₂)';
       return this.massA > this.massB ? '左侧偏重 (m₁ > m₂)' : '右侧偏重 (m₁ < m₂)';
    }
  },
  watch: {
    targetAngle() {
       this.startAnimate();
    }
  },
  mounted() {
    this.startAnimate();
  },
  beforeDestroy() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
  },
  methods: {
    startAnimate() {
       if (!this.animFrame) {
         this.animate();
       }
    },
    animate() {
       const diff = this.targetAngle - this.currentAngle;
       if (Math.abs(diff) < 0.1) {
          this.currentAngle = this.targetAngle;
          this.animFrame = null;
          return;
       }
       this.currentAngle += diff * 0.1;
       this.animFrame = requestAnimationFrame(this.animate);
    }
  }
}
</script>

<style scoped lang="scss">
.mass-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #10b981; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.main-layout { display: flex; gap: 12px; height: 100%; min-height: 0; flex-direction: column; }
.simulation-stage { flex: 1; min-height: 180px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; display: flex; justify-content: center; align-items: center; 
  .hud-panel-pro { position: absolute; top: 12px; left: 12px; display: flex; gap: 12px; z-index: 10;
     .hud-col { display: flex; flex-direction: column; gap: 6px; 
       .item { background: rgba(30,30,30,0.6); backdrop-filter: blur(4px); border-radius: 8px; padding: 6px 12px; border: 1px solid rgba(255,255,255,0.1); 
         label { display: block; font-size: 11px; color: #94a3b8; font-weight: 600; margin-bottom: 2px;} 
         span { font-size: 13px; color: #f8fafc; font-weight: 600; } 
         &.big span { font-size: 16px; font-weight: 800; } 
       } 
     } 
  } 
}
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: -5px; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { font-style: normal; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
